import { printHtml } from "@/components/inventory/shelfPrint";
import html2pdf from "html2pdf.js";
import { WATERCO_LOGO } from "@/assets/watercoLogo";

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const fmtN = (n, d = 0) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const usd = (n) => `USD ${fmtN(n, 2)}`;
const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(String(s).length <= 10 ? `${s}T00:00:00` : s);
  return isNaN(d) ? s : `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const BLUE = "#2323a8";

const BANK_LINES = [
  "Instructions for wire payments:",
  "Bank Name: JPMorgan Chase Bank N.A.",
  "Bank Address: 383 Madison Ave",
  "New York, NY 10017",
  "ABA #: 021000021",
  "Account Name: Waterco LLC",
  "Account #: 953718512",
  "Please instruct your bank to debit the wire fee from",
  "your account or include the fee with your payment",
];

const HQ_ADDRESS = ["575 5th Ave, 31st Floor", "New York, NY 10017"];

const CSS = `
    .inv * { box-sizing: border-box; }
    .inv { font-family: 'Times New Roman', Times, serif; color: #000; font-size: 11px; line-height: 1.35; width: 100%; }
    .inv .lbl { font-family: Arial, Helvetica, sans-serif; }
    .inv .top { display: flex; justify-content: space-between; align-items: flex-start; }
    .inv .logo img { width: 230px; display: block; }
    .inv .hq { color: ${BLUE}; font-size: 10.5px; margin-top: 6px; margin-left: 10px; }
    .inv .title { font-family: Arial, Helvetica, sans-serif; font-size: 27px; font-weight: 700; color: #000; text-align: right; letter-spacing: 0.01em; }
    .inv table.meta { border-collapse: collapse; margin-top: 16px; margin-left: auto; }
    .inv table.meta th { font-family: Arial, Helvetica, sans-serif; font-weight: 400; font-size: 10.5px; border: 1px solid #000; padding: 3px 10px; text-align: center; min-width: 78px; }
    .inv table.meta td { border: 1px solid #000; padding: 5px 10px; text-align: center; font-size: 11px; }
    .inv .mid { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 16px; }
    .inv .billto { width: 46%; border: 1px solid #000; min-height: 118px; }
    .inv .billto .bhdr { font-family: Arial, Helvetica, sans-serif; font-size: 10.5px; border-bottom: 1px solid #000; padding: 3px 10px; }
    .inv .billto .blines { padding: 6px 10px; font-size: 10.5px; }
    .inv .bank { width: 48%; border: 1px solid #000; text-align: center; font-size: 12px; padding: 4px 6px 6px; line-height: 1.32; }
    .inv .bank .bt { font-size: 12.5px; }
    .inv table.po { border-collapse: collapse; margin-top: 24px; }
    .inv table.po th { font-family: Arial, Helvetica, sans-serif; font-weight: 400; font-size: 10.5px; border: 1px solid #000; padding: 3px 12px; text-align: center; min-width: 96px; }
    .inv table.po td { border: 1px solid #000; padding: 5px 12px; text-align: center; font-size: 10.5px; }
    .inv table.items { width: 100%; border-collapse: collapse; margin-top: 6px; }
    .inv table.items th { font-family: Arial, Helvetica, sans-serif; font-weight: 400; font-size: 10.5px; border: 1px solid #000; padding: 3px 6px; text-align: center; }
    .inv table.items td { border-left: 1px solid #000; border-right: 1px solid #000; padding: 3px 6px; vertical-align: top; font-size: 10.5px; }
    .inv table.items .fill td { border-bottom: 1px solid #000; }
    .inv .r { text-align: right !important; font-variant-numeric: tabular-nums; }
    .inv table.tot { width: 100%; border-collapse: collapse; }
    .inv table.tot td { border: 1px solid #000; }
    .inv .totlbl { font-family: Arial, Helvetica, sans-serif; font-weight: 700; font-size: 13px; }
    .inv .totamt { font-size: 10.5px; text-align: right; font-variant-numeric: tabular-nums; }
    .inv .sidebox { width: 43%; margin-left: auto; border: 1px solid #000; border-top: none; display: flex; justify-content: space-between; align-items: flex-end; padding: 6px 8px 5px; }
    .inv .bal .totlbl { font-size: 16px; }
`;

const buildBody = (inv, so, company = {}, customer = null) => {
  const lines = inv.lines || [];
  const paid = (inv.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const itemText = (l) => [l.design, l.description, l.pack].map((s) => String(s || "").trim()).filter(Boolean).join(" ");
  const itemCode = (l) => {
    const codes = [l.case_sku, l.case_upc].map((s) => String(s || "").trim()).filter(Boolean);
    return codes.length ? codes.map(esc).join("<br/>") : esc(itemText(l));
  };
  const rows = lines.map((l) => `
    <tr>
      <td class="r" style="width:14%">${fmtN(l.cases)}</td>
      <td style="width:26%">${itemCode(l)}</td>
      <td style="width:28%">${esc(itemText(l))}</td>
      <td class="r" style="width:16%">${fmtN(l.price, 2)}</td>
      <td class="r" style="width:16%">${fmtN((l.cases || 0) * (l.price || 0), 2)}</td>
    </tr>`).join("");
  const notesRow = (inv.notes || "").trim()
    ? `<tr><td></td><td colspan="2" style="font-style:italic">${esc(inv.notes).replace(/\n/g, "<br/>")}</td><td></td><td></td></tr>` : "";
  const fillH = Math.max(40, 470 - lines.length * 34 - ((inv.notes || "").trim() ? 30 : 0));
  const billLines = [customer?.company_name || so.customer_name, ...String(customer?.address || "").split("\n")]
    .map((s) => String(s || "").trim()).filter(Boolean);
  return `
    <div class="top">
      <div>
        <div class="logo"><img src="${WATERCO_LOGO}" alt="waterco" /></div>
        <div class="hq">${HQ_ADDRESS.map((l) => `<div>${esc(l)}</div>`).join("")}</div>
      </div>
      <div>
        <div class="title">Invoice</div>
        <table class="meta">
          <tr><th>Date</th><th>Invoice #</th></tr>
          <tr><td>${fmtDate(inv.invoice_date)}</td><td>${esc(inv.number)}</td></tr>
        </table>
      </div>
    </div>
    <div class="mid">
      <div class="billto">
        <div class="bhdr">Bill To</div>
        <div class="blines">${billLines.map((l) => `<div>${esc(l)}</div>`).join("")}</div>
      </div>
      <div class="bank">
        <div class="bt">Bank information</div>
        ${BANK_LINES.map((l) => `<div>${esc(l)}</div>`).join("")}
      </div>
    </div>
    <table class="po">
      <tr><th>P.O. Number</th><th>Terms</th><th>Due Date</th></tr>
      <tr><td>${esc(so.customer_po || "")}</td><td>${esc(so.terms || "")}</td><td>${fmtDate(inv.due_date)}</td></tr>
    </table>
    <table class="items">
      <tr>
        <th style="width:14%">Quantity</th><th style="width:26%">Item Code</th><th style="width:28%">Description</th><th style="width:16%">Price Each</th><th style="width:16%">Amount</th>
      </tr>
      ${rows}
      ${notesRow}
      <tr class="fill"><td style="height:${fillH}px"></td><td></td><td></td><td></td><td></td></tr>
    </table>
    <table class="tot">
      <tr>
        <td style="width:57%"></td>
        <td style="width:43%;border-top:none;padding:6px 8px 5px">
          <div style="display:flex;justify-content:space-between;align-items:flex-end">
            <span class="totlbl">Total</span><span class="totamt">${usd(inv.total)}</span>
          </div>
        </td>
      </tr>
    </table>
    <div class="sidebox">
      <span class="totlbl">Payments/Credits</span><span class="totamt">${usd(paid ? -paid : 0)}</span>
    </div>
    <div class="sidebox bal">
      <span class="totlbl">Balance Due</span><span class="totamt">${usd((inv.total || 0) - paid)}</span>
    </div>`;
};

export const printInvoice = (inv, so, company = {}, customer = null) => {
  const html = `<html><head><title>${esc(inv.number)} — Invoice</title><style>
    body { margin: 24px; }
    ${CSS}
    @media print { body { margin: 10mm; } }
  </style></head><body><div class="inv">${buildBody(inv, so, company, customer)}</div></body></html>`;
  printHtml(html);
};

export const downloadInvoicePdf = async (inv, so, company = {}, customer = null) => {
  const wrap = document.createElement("div");
  wrap.style.cssText = "position:fixed;left:-10000px;top:0;width:790px;background:#ffffff;";
  wrap.innerHTML = `<style>${CSS}</style><div class="inv">${buildBody(inv, so, company, customer)}</div>`;
  document.body.appendChild(wrap);
  try {
    await html2pdf().set({
      margin: [10, 10, 10, 10],
      filename: `${inv.number || "invoice"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
      jsPDF: { unit: "mm", format: "letter", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] },
    }).from(wrap.querySelector(".inv")).save();
  } finally {
    wrap.remove();
  }
};
